import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { TrackContext } from '../types';
import { api } from '../services/api';

interface TrackCacheContextType {
  // Get a track from cache (returns undefined if not cached)
  getTrack: (trackId: string) => TrackContext | undefined;

  // Get a track, fetching from API if not cached (use forceRefresh to bypass cache)
  fetchTrack: (trackId: string, forceRefresh?: boolean) => Promise<TrackContext | null>;

  // Set a track in cache (e.g., after upload completes)
  setTrack: (track: TrackContext) => void;

  // Set multiple tracks (e.g., after listing tracks)
  setTracks: (tracks: TrackContext[]) => void;

  // Clear a track from cache
  clearTrack: (trackId: string) => void;

  // Clear all cached tracks
  clearAll: () => void;
}

const TrackCacheContext = createContext<TrackCacheContextType | null>(null);

export function TrackCacheProvider({ children }: { children: ReactNode }) {
  const [cache, setCache] = useState<Map<string, TrackContext>>(new Map());

  const getTrack = useCallback((trackId: string) => {
    return cache.get(trackId);
  }, [cache]);

  const fetchTrack = useCallback(async (trackId: string, forceRefresh = false): Promise<TrackContext | null> => {
    // Check cache first (unless forceRefresh is true)
    if (!forceRefresh) {
      const cached = cache.get(trackId);
      if (cached) {
        console.log('[TrackCache] Cache hit for:', trackId);
        return cached;
      }
    }

    // Try fast insights endpoint first (<200ms)
    try {
      console.log('[TrackCache] Fetching insights for:', trackId);
      const insightsResponse = await api.getTrackInsights(trackId);

      if (insightsResponse.data?.success) {
        const insights = insightsResponse.data;
        console.log(`[TrackCache] Fast insights retrieved in ${insights.response_ms}ms`);

        // Build TrackContext from insights
        const track: TrackContext = {
          id: trackId,
          persona_id: insights.persona_id,
          filename: insights.audio?.filename || 'unknown',
          display_name: insights.audio?.display_name || insights.audio?.filename || null,
          file_url: insights.audio?.file_url,
          duration_seconds: insights.audio?.duration_seconds,
          primary_genre: insights.audio?.primary_genre || null,
          primary_mood: insights.audio?.primary_mood || null,
          tempo_bpm: insights.audio?.tempo_bpm,
          key_signature: insights.audio?.key_signature,
          cultural_context: insights.audio?.cultural_context || null,
          analyzed_at: insights.audio?.analyzed_at || null,
          processing_status: insights.status?.processing,
          processing_error: insights.status?.processing_error || null,
          enhancement_status: insights.status?.enhancement,
          created_at: insights.audio?.created_at || new Date().toISOString(),
          context_data: {
            timeline: {
              energy_curve: insights.analysis?.energy_curve || [],
              mood_indicators: insights.analysis?.mood_indicators || {},
              beats: insights.deep?.beats,
              downbeats: insights.deep?.downbeats,
              section_map: insights.deep?.sections,
              mood_timeline: insights.deep?.mood_timeline,
            },
            transcription: insights.transcription,
            musical_intelligence: insights.ai_synthesis?.musical_intelligence,
            visual_recommendations: insights.ai_synthesis?.visual_recommendations,
          },
          _insights: insights,
        };

        // Cache the result
        setCache(prev => {
          const newCache = new Map(prev);
          newCache.set(trackId, track);
          return newCache;
        });

        return track;
      }
    } catch (err) {
      console.warn('[TrackCache] Fast insights failed, falling back to API:', err);
    }

    // Fallback to standard API
    try {
      const response = await api.getTrack(trackId);
      if (response.error || !response.data) {
        console.error('Failed to fetch track:', response.error);
        return null;
      }

      // Cache the result
      setCache(prev => {
        const newCache = new Map(prev);
        newCache.set(trackId, response.data!);
        return newCache;
      });

      return response.data;
    } catch (err) {
      console.error('Failed to fetch track:', err);
      return null;
    }
  }, [cache]);

  const setTrack = useCallback((track: TrackContext) => {
    setCache(prev => {
      const newCache = new Map(prev);
      newCache.set(track.id, track);
      return newCache;
    });
  }, []);

  const setTracks = useCallback((tracks: TrackContext[]) => {
    setCache(prev => {
      const newCache = new Map(prev);
      tracks.forEach(track => {
        newCache.set(track.id, track);
      });
      return newCache;
    });
  }, []);

  const clearTrack = useCallback((trackId: string) => {
    setCache(prev => {
      const newCache = new Map(prev);
      newCache.delete(trackId);
      return newCache;
    });
  }, []);

  const clearAll = useCallback(() => {
    setCache(new Map());
  }, []);

  return (
    <TrackCacheContext.Provider value={{
      getTrack,
      fetchTrack,
      setTrack,
      setTracks,
      clearTrack,
      clearAll,
    }}>
      {children}
    </TrackCacheContext.Provider>
  );
}

export function useTrackCache() {
  const context = useContext(TrackCacheContext);
  if (!context) {
    throw new Error('useTrackCache must be used within a TrackCacheProvider');
  }
  return context;
}
